/**
 * OfferDiceChooser — the loadout picker shown over the "try the dice" scene.
 *
 * A top panel with 3 shooting SLOTS and, below them, a PALETTE of the offer's
 * dice. The player drags a die from the palette onto a slot to build a combo,
 * then shoots to see how it feels. Slot thumbnails match the dice library
 * fidelity (CDN thumb over a base plate, drop-shadow).
 *
 * Lifecycle UX (driven by the controller):
 *   - opens EXPANDED the first time (so the player sees the chooser),
 *   - COLLAPSES into a round dice button the moment dice are picked up / rolling
 *     (so it's out of the way during the throw),
 *   - the round button re-EXPANDS it on tap, so the player can swap dice and
 *     throw again.
 *
 * Not shown at all when the offer has a single die (nothing to choose).
 *
 * Body-level overlay with injected CSS, mirroring OfferPopup.
 */

const STYLE_ID = 'sdl-offerchooser-styles';
const SLOT_COUNT = 3;
const FALLBACK_THUMB = '/assets/ui/dice-thumb-placeholder.png';
const BASE_PLATE = '/assets/ui/dice-base.png';

export interface OfferDiceChooserDeps {
  /** Build the CDN thumbnail URL for a dice code. */
  thumbUrl: (diceId: string) => string;
  /** Fired with the new 3-slot loadout whenever a slot changes. */
  onChange: (slots: string[]) => void;
}

export class OfferDiceChooser {
  public element: HTMLElement;
  private slotsRow: HTMLElement;
  private paletteRow: HTMLElement;
  private collapsedBtn: HTMLButtonElement;
  private ghost: HTMLImageElement;
  private deps: OfferDiceChooserDeps;

  private offerDice: string[] = [];
  private slots: string[] = [];
  private collapsed = false;

  // Drag state.
  private dragging = false;
  private dragDiceId: string | null = null;

  constructor(deps: OfferDiceChooserDeps) {
    this.deps = deps;
    OfferDiceChooser.injectStyles();

    this.element = document.createElement('div');
    this.element.className = 'sdl-offerchooser-host';
    this.element.innerHTML = `
      <div class="sdl-offerchooser-panel">
        <div class="sdl-offerchooser-title">Pick your dice</div>
        <div class="sdl-offerchooser-slots"></div>
        <div class="sdl-offerchooser-hint">Drag a die up into a slot</div>
        <div class="sdl-offerchooser-palette"></div>
      </div>
      <button class="sdl-offerchooser-collapsed" aria-label="Change dice">
        <img alt="" />
      </button>
      <img class="sdl-offerchooser-ghost" alt="" />
    `;
    document.body.appendChild(this.element);

    this.slotsRow = this.element.querySelector('.sdl-offerchooser-slots') as HTMLElement;
    this.paletteRow = this.element.querySelector('.sdl-offerchooser-palette') as HTMLElement;
    this.collapsedBtn = this.element.querySelector('.sdl-offerchooser-collapsed') as HTMLButtonElement;
    this.ghost = this.element.querySelector('.sdl-offerchooser-ghost') as HTMLImageElement;

    this.collapsedBtn.addEventListener('click', () => this.expand());

    // Global pointer move/up so a drag that leaves a palette tile still tracks.
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    document.addEventListener('pointermove', this.onPointerMove, { passive: false });
    document.addEventListener('pointerup', this.onPointerUp);
  }

  /**
   * Seed the chooser from the offer's dice. Auto-fills the 3 slots:
   *   >= 3 dice -> [0,1,2]; 2 dice -> [0,1,0]; (1 die is handled by the caller,
   *   which doesn't show the chooser at all).
   */
  setOffer(offerDice: string[]): void {
    this.offerDice = offerDice.filter(Boolean);
    const d = this.offerDice;
    if (d.length >= SLOT_COUNT) {
      this.slots = [d[0], d[1], d[2]];
    } else if (d.length === 2) {
      this.slots = [d[0], d[1], d[0]];
    } else {
      this.slots = [d[0], d[0], d[0]];
    }
    this.renderPalette();
    this.renderSlots();
  }

  getSlots(): string[] {
    return [...this.slots];
  }

  isCollapsed(): boolean {
    return this.collapsed;
  }

  show(): void {
    this.element.classList.add('visible');
  }

  hide(): void {
    this.element.classList.remove('visible');
  }

  collapse(): void {
    if (this.collapsed) return;
    this.collapsed = true;
    this.element.classList.add('collapsed');
    const img = this.collapsedBtn.querySelector('img') as HTMLImageElement;
    img.src = this.thumb(this.slots[0]);
  }

  expand(): void {
    if (!this.collapsed) return;
    this.collapsed = false;
    this.element.classList.remove('collapsed');
  }

  destroy(): void {
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    this.element.remove();
  }

  // ---- rendering ---------------------------------------------------------

  private thumb(id: string | undefined): string {
    return id ? this.deps.thumbUrl(id) : FALLBACK_THUMB;
  }

  private renderSlots(): void {
    this.slotsRow.innerHTML = this.slots
      .map(
        (id, i) => `
        <div class="sdl-offerchooser-slot" data-slot="${i}">
          <img class="sdl-offerchooser-slot-base" src="${BASE_PLATE}" alt="" onerror="this.style.display='none'">
          <img class="sdl-offerchooser-slot-thumb" src="${this.thumb(id)}"
               onerror="this.onerror=null;this.src='${FALLBACK_THUMB}'" alt="">
        </div>`,
      )
      .join('');
  }

  private renderPalette(): void {
    this.paletteRow.innerHTML = this.offerDice
      .map(
        (id) => `
        <div class="sdl-offerchooser-tile" data-dice="${id}">
          <img src="${this.thumb(id)}" onerror="this.onerror=null;this.src='${FALLBACK_THUMB}'"
               alt="" draggable="false">
        </div>`,
      )
      .join('');
    this.paletteRow.querySelectorAll<HTMLElement>('.sdl-offerchooser-tile').forEach((tile) => {
      tile.addEventListener('pointerdown', (e) => this.onTilePointerDown(e, tile));
    });
  }

  // ---- drag --------------------------------------------------------------

  private onTilePointerDown(e: PointerEvent, tile: HTMLElement): void {
    const id = tile.dataset.dice;
    if (!id) return;
    e.preventDefault();
    this.dragging = true;
    this.dragDiceId = id;
    tile.classList.add('dragging-source');
    this.ghost.src = this.thumb(id);
    this.ghost.classList.add('visible');
    this.moveGhost(e.clientX, e.clientY);
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.dragging) return;
    e.preventDefault();
    this.moveGhost(e.clientX, e.clientY);
    this.slotsRow.querySelectorAll<HTMLElement>('.sdl-offerchooser-slot').forEach((slot) => {
      slot.classList.toggle('drop-hover', this.pointInRect(e.clientX, e.clientY, slot));
    });
  }

  private onPointerUp(e: PointerEvent): void {
    if (!this.dragging) return;
    let target = -1;
    this.slotsRow.querySelectorAll<HTMLElement>('.sdl-offerchooser-slot').forEach((slot, i) => {
      slot.classList.remove('drop-hover');
      if (this.pointInRect(e.clientX, e.clientY, slot)) target = i;
    });
    if (target >= 0 && this.dragDiceId) {
      this.slots[target] = this.dragDiceId;
      this.renderSlots();
      this.deps.onChange(this.getSlots());
    }
    this.paletteRow
      .querySelector('.dragging-source')
      ?.classList.remove('dragging-source');
    this.dragging = false;
    this.dragDiceId = null;
    this.ghost.classList.remove('visible');
  }

  private moveGhost(x: number, y: number): void {
    this.ghost.style.left = `${x}px`;
    this.ghost.style.top = `${y}px`;
  }

  private pointInRect(x: number, y: number, el: HTMLElement): boolean {
    const r = el.getBoundingClientRect();
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  // ---- styles ------------------------------------------------------------

  private static injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .sdl-offerchooser-host {
        position: fixed; top: 0; left: 0; right: 0; z-index: 210;
        display: none; flex-direction: column; align-items: center;
        pointer-events: none;
      }
      .sdl-offerchooser-host.visible { display: flex; }

      .sdl-offerchooser-panel {
        pointer-events: auto;
        /* Clear the scene's top-right close button (42px @ top 14px + gap). */
        margin-top: calc(env(safe-area-inset-top, 0px) + 64px);
        padding: 14px 18px 16px;
        background: rgba(10, 10, 14, 0.92);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 18px;
        box-shadow: 0 18px 44px rgba(0, 0, 0, 0.6);
        backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
        animation: sdl-offerchooser-in 0.26s cubic-bezier(0.34, 1.4, 0.6, 1) both;
      }
      .sdl-offerchooser-host.collapsed .sdl-offerchooser-panel { display: none; }
      @keyframes sdl-offerchooser-in {
        from { transform: translateY(-14px); opacity: 0; }
        to   { transform: translateY(0); opacity: 1; }
      }

      .sdl-offerchooser-title {
        text-align: center;
        font-family: 'Bebas Neue', sans-serif;
        letter-spacing: 1px; font-size: 20px;
        color: var(--gold, #FFD700);
        margin-bottom: 8px;
      }
      .sdl-offerchooser-hint {
        text-align: center;
        font-family: 'Rajdhani', sans-serif; font-weight: 600;
        font-size: 11px; letter-spacing: 0.6px;
        color: rgba(255, 255, 255, 0.6);
        margin: 8px 0 6px;
      }

      .sdl-offerchooser-slots { display: flex; gap: 12px; justify-content: center; }
      .sdl-offerchooser-slot {
        position: relative; width: 5.4rem; height: 6rem;
        display: flex; align-items: flex-end; justify-content: center;
        border-radius: 12px;
        transition: transform 0.12s ease;
      }
      .sdl-offerchooser-slot.drop-hover {
        transform: scale(1.06);
        box-shadow: 0 0 0 2px var(--cyan, #00F5FF), 0 0 18px rgba(0, 245, 255, 0.5);
        background: rgba(0, 245, 255, 0.08);
      }
      .sdl-offerchooser-slot-base {
        position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
        width: 6rem; height: auto; opacity: 0.9; pointer-events: none;
      }
      .sdl-offerchooser-slot-thumb {
        position: relative; width: 4.6rem; height: auto;
        margin-bottom: 0.8rem;
        filter: drop-shadow(2px 2px 2px rgba(0, 0, 0, 0.9));
        pointer-events: none;
      }

      .sdl-offerchooser-palette {
        display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;
        max-width: min(86vw, 420px);
      }
      .sdl-offerchooser-tile {
        width: 3.4rem; height: 3.4rem;
        display: flex; align-items: center; justify-content: center;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 12px;
        touch-action: none; cursor: grab;
        transition: transform 0.12s ease, background 0.15s ease;
      }
      .sdl-offerchooser-tile:active { cursor: grabbing; }
      .sdl-offerchooser-tile.dragging-source { opacity: 0.4; }
      .sdl-offerchooser-tile img {
        width: 2.9rem; height: auto;
        filter: drop-shadow(2px 3px 4px rgba(0, 0, 0, 0.9));
        -webkit-user-drag: none; user-select: none;
      }

      .sdl-offerchooser-collapsed {
        pointer-events: auto; display: none;
        margin-top: calc(env(safe-area-inset-top, 0px) + 64px);
        width: 60px; height: 60px; padding: 0;
        border-radius: 50%;
        background: rgba(10, 10, 14, 0.92);
        border: 1px solid rgba(255, 255, 255, 0.14);
        box-shadow: 0 10px 28px rgba(0, 0, 0, 0.6);
        cursor: pointer; -webkit-tap-highlight-color: transparent;
        transition: transform 0.12s ease;
        animation: sdl-offerchooser-in 0.22s ease both;
      }
      .sdl-offerchooser-host.collapsed .sdl-offerchooser-collapsed { display: flex; align-items: center; justify-content: center; }
      .sdl-offerchooser-collapsed:active { transform: scale(0.92); }
      .sdl-offerchooser-collapsed img {
        width: 38px; height: 38px;
        filter: drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.9));
      }

      .sdl-offerchooser-ghost {
        position: fixed; z-index: 320;
        width: 3.2rem; height: auto;
        transform: translate(-50%, -50%);
        filter: drop-shadow(2px 4px 6px rgba(0, 0, 0, 0.9));
        pointer-events: none; display: none;
      }
      .sdl-offerchooser-ghost.visible { display: block; }
    `;
    document.head.appendChild(style);
  }
}
